import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-md active:scale-[0.98]",
        outline:
          "border border-input bg-background/60 backdrop-blur-sm hover:bg-accent/10 hover:text-accent hover:border-accent/40 active:scale-[0.98]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 hover:shadow-sm active:scale-[0.98]",
        ghost: "hover:bg-accent/10 hover:text-accent active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline",
        tropical: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md active:scale-[0.98]",
        hero: "bg-background/70 backdrop-blur-sm text-foreground border border-border/40 hover:bg-background/90 hover:border-primary/40 active:scale-[0.98]",
        teal: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md active:scale-[0.98]",
        orange: "bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-md active:scale-[0.98]",
        navy: "bg-secondary text-secondary-foreground hover:bg-secondary/90 hover:shadow-md active:scale-[0.98]",
        gold: "bg-[hsl(var(--highlight))] text-[hsl(var(--highlight-foreground))] hover:brightness-95 hover:shadow-md active:scale-[0.98]",
        celestial:
          "overflow-hidden celestial-corner bg-primary text-primary-foreground pr-10 hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4",
        lg: "h-12 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
